/**
 * WorkbenchToolCards — WorkbenchChat 전용 도구 호출 커스텀 카드 4종.
 *
 * 각 도구는 ChatThread 의 toolUI 매핑에 등록되어 기본 ToolCallCard 를 대체한다.
 * - search_blocks : 블럭 검색 결과 + Optimistic 그래프 반영
 * - search_packs  : 팩 검색 결과 (read-only)
 * - suggest_slice : 슬라이스 제안 미리보기 + Optimistic 콜백
 * - save_slice_to_service : confirm 모드 pending 처리 + [자동 적용으로 전환]
 *
 * Pending 상태는 assistant-ui 의 tool-call status "requires-action" 이 아닌
 * output.pending 플래그로 구분한다(서버가 `{ ..., pending: true }` 를 반환하는 방식).
 * 실제 "적용" 은 부모 WorkbenchChat 의 applyPendingTool 콜백으로 위임한다.
 * 사용자가 무시하려면 새 메시지를 보내거나 채팅을 초기화하면 된다 (별도 skip 버튼 없음).
 */
import { useEffect } from "react"
import type { ToolCallRenderProps } from "@/shared/chat"
import type { SlicePreview } from "./WorkbenchSlicePreview.tsx"
import { extractItemList, extractStringArray, summarizeValue, toolLabel } from "./workbench-helpers.ts"

// ─── 공용 타입 ────────────────────────────────────────────────────────────────

/** WorkbenchChat 에서 각 카드 팩토리에 주입하는 콜백 모음. */
export interface WorkbenchCardCallbacks {
	/** search_blocks 결과 블럭 ID 배열 → 그래프 즉시 반영. */
	readonly onSearchBlocksResult?: (blockIds: ReadonlyArray<string>) => void
	/** suggest_slice 결과 preview → 슬라이스 미리보기 카드 표시 + 그래프 반영. */
	readonly onSuggestSliceResult?: (preview: SlicePreview) => void
	/**
	 * save_slice_to_service pending 시 "적용" 버튼 클릭 콜백.
	 * toolCallId 를 받아 상위에서 auto 모드 재전송을 트리거한다.
	 */
	readonly onApplyPendingTool?: (toolCallId: string) => void
}

// ─── search_blocks 카드 ────────────────────────────────────────────────────────

/**
 * SearchBlocksCard — search_blocks 도구 결과 카드.
 *
 * complete 상태에서 result 의 blocks 를 onSearchBlocksResult 로 Optimistic 전달한다.
 * 목록 표시는 최대 5개로 제한해 카드 높이를 적당히 유지한다.
 */
export function createSearchBlocksCard(callbacks: WorkbenchCardCallbacks) {
	return function SearchBlocksCard(props: ToolCallRenderProps) {
		const { result, status } = props

		// complete 상태 + result 있을 때 Optimistic 그래프 반영
		useEffect(() => {
			if (status !== "complete" || !result) return
			const ids = extractStringArray(result)
			if (ids.length > 0) callbacks.onSearchBlocksResult?.(ids)
		}, [status, result])

		if (status === "in_progress") {
			return (
				<div className="workbench-tool-card workbench-tool-card--running">
					<span className="workbench-tool-card__label">{toolLabel("search_blocks")}</span>
					<span className="workbench-tool-card__status">검색 중…</span>
				</div>
			)
		}

		// 결과 블럭 목록
		const blocks = extractItemList(result, "blocks", ["id", "displayName"])
		const showCount = 5
		const shown = blocks.slice(0, showCount)
		const extra = blocks.length - showCount

		return (
			<div className="workbench-tool-card workbench-tool-card--ok">
				<div className="workbench-tool-card__head">
					<span className="workbench-tool-card__label">{toolLabel("search_blocks")}</span>
					<span className="workbench-tool-card__count">{blocks.length}개 블럭</span>
				</div>
				{blocks.length === 0 ? (
					<span className="workbench-tool-card__empty">검색 결과 없음</span>
				) : (
					<ul className="workbench-tool-card__list">
						{shown.map((b) => (
							<li key={b.id} className="workbench-tool-card__list-item">
								<span className="workbench-tool-card__item-id">{b.id}</span>
								{b.displayName && <span className="workbench-tool-card__item-name">{b.displayName}</span>}
							</li>
						))}
						{extra > 0 && <li className="workbench-tool-card__list-more">+{extra}개 더</li>}
					</ul>
				)}
				{props.result !== undefined && (
					<details className="workbench-tool-card__details">
						<summary className="workbench-tool-card__summary">원본 결과 보기</summary>
						<pre className="workbench-tool-card__json">{summarizeValue(props.result, 300)}</pre>
					</details>
				)}
			</div>
		)
	}
}

// ─── search_packs 카드 ────────────────────────────────────────────────────────

/**
 * SearchPacksCard — search_packs 도구 결과 카드 (read-only).
 *
 * 팩 목록을 최대 5개 렌더한다. Optimistic 콜백 없음.
 */
export function createSearchPacksCard(_callbacks: WorkbenchCardCallbacks) {
	return function SearchPacksCard(props: ToolCallRenderProps) {
		const { result, status } = props

		if (status === "in_progress") {
			return (
				<div className="workbench-tool-card workbench-tool-card--running">
					<span className="workbench-tool-card__label">{toolLabel("search_packs")}</span>
					<span className="workbench-tool-card__status">검색 중…</span>
				</div>
			)
		}

		const packs = extractItemList(result, "packs", ["id", "displayName", "icon"])
		const showCount = 5
		const shown = packs.slice(0, showCount)
		const extra = packs.length - showCount

		return (
			<div className="workbench-tool-card workbench-tool-card--ok">
				<div className="workbench-tool-card__head">
					<span className="workbench-tool-card__label">{toolLabel("search_packs")}</span>
					<span className="workbench-tool-card__count">{packs.length}개 팩</span>
				</div>
				{packs.length === 0 ? (
					<span className="workbench-tool-card__empty">검색 결과 없음</span>
				) : (
					<ul className="workbench-tool-card__list">
						{shown.map((p) => (
							<li key={p.id} className="workbench-tool-card__list-item">
								{p.icon && <span className="workbench-tool-card__item-icon">{p.icon}</span>}
								<span className="workbench-tool-card__item-id">{p.id}</span>
								{p.displayName && <span className="workbench-tool-card__item-name">{p.displayName}</span>}
							</li>
						))}
						{extra > 0 && <li className="workbench-tool-card__list-more">+{extra}개 더</li>}
					</ul>
				)}
			</div>
		)
	}
}

// ─── suggest_slice 카드 ───────────────────────────────────────────────────────

/**
 * SuggestSliceCard — suggest_slice 도구 결과 카드.
 *
 * complete 상태에서 result.preview 를 onSuggestSliceResult 로 Optimistic 전달한다.
 * 카드 자체에서 슬라이스 구성 요약(kinds/relations/shapes 개수)을 보여준다.
 * 상세 미리보기는 BlocksPage 의 WorkbenchSlicePreview 카드가 담당한다.
 */
export function createSuggestSliceCard(callbacks: WorkbenchCardCallbacks) {
	return function SuggestSliceCard(props: ToolCallRenderProps) {
		const { result, status } = props

		useEffect(() => {
			if (status !== "complete" || !result) return
			const preview = extractSlicePreview(result)
			if (preview) callbacks.onSuggestSliceResult?.(preview)
		}, [status, result])

		if (status === "in_progress") {
			return (
				<div className="workbench-tool-card workbench-tool-card--running">
					<span className="workbench-tool-card__label">{toolLabel("suggest_slice")}</span>
					<span className="workbench-tool-card__status">슬라이스 제안 중…</span>
				</div>
			)
		}

		const preview = result ? extractSlicePreview(result) : null
		const kindsLen = preview?.kinds.length ?? 0
		const relationsLen = preview?.relations.length ?? 0
		const shapesLen = preview?.shapes.length ?? 0

		return (
			<div className="workbench-tool-card workbench-tool-card--ok">
				<div className="workbench-tool-card__head">
					<span className="workbench-tool-card__label">{toolLabel("suggest_slice")}</span>
					{preview && (
						<span className="workbench-tool-card__count">
							kinds {kindsLen} · relations {relationsLen} · shapes {shapesLen}
						</span>
					)}
				</div>
				{preview && (
					<p className="workbench-tool-card__hint">
						왼쪽 그래프에서 kinds {kindsLen}개 블럭이 하이라이트됩니다.
					</p>
				)}
				{!preview && <span className="workbench-tool-card__empty">슬라이스 제안 없음</span>}
			</div>
		)
	}
}

// ─── save_slice_to_service 카드 ───────────────────────────────────────────────

/**
 * SaveSliceCard — save_slice_to_service 도구 카드.
 *
 * confirm 모드에서 output.pending === true 이면 [다음 메시지에서 자동 적용] 버튼을 표시.
 * 클릭 → onApplyPendingTool(toolCallId) 로 confirmMode 가 "auto" 로 전환되며 다음 메시지 전송 시 저장된다.
 * 무시하려면 새 메시지를 보내거나 채팅을 초기화한다 (별도 skip 버튼 없음).
 * auto 모드 또는 저장 완료 후에는 결과만 표시한다.
 *
 * pending 감지: result 객체 내 `pending: true` 필드 확인.
 */
export function createSaveSliceCard(callbacks: WorkbenchCardCallbacks, confirmMode: "confirm" | "auto") {
	return function SaveSliceCard(props: ToolCallRenderProps) {
		const { toolCallId, result, status } = props

		if (status === "in_progress") {
			return (
				<div className="workbench-tool-card workbench-tool-card--running">
					<span className="workbench-tool-card__label">{toolLabel("save_slice_to_service")}</span>
					<span className="workbench-tool-card__status">저장 중…</span>
				</div>
			)
		}

		// pending 감지 — result.pending === true 이고 confirmMode === "confirm" 일 때
		const isPending =
			confirmMode === "confirm" &&
			result !== null &&
			result !== undefined &&
			typeof result === "object" &&
			(result as Record<string, unknown>).pending === true

		if (isPending) {
			return (
				<div className="workbench-tool-card workbench-tool-card--pending">
					<div className="workbench-tool-card__head">
						<span className="workbench-tool-card__label">{toolLabel("save_slice_to_service")}</span>
						<span className="workbench-tool-card__badge is-pending-badge">대기 중</span>
					</div>
					<p className="workbench-tool-card__hint">슬라이스를 서비스에 저장할까요?</p>
					{result && (
						<details className="workbench-tool-card__details">
							<summary className="workbench-tool-card__summary">상세 보기</summary>
							<pre className="workbench-tool-card__json">{summarizeValue(result, 300)}</pre>
						</details>
					)}
					<div className="workbench-tool-card__actions">
						<button
							type="button"
							className="btn btn-primary btn-sm"
							onClick={() => callbacks.onApplyPendingTool?.(toolCallId)}
							title="자동 적용 모드로 전환되며, 다음 메시지 전송 시 저장됩니다"
						>
							다음 메시지에서 자동 적용
						</button>
					</div>
					<p className="workbench-tool-card__hint workbench-tool-card__hint--muted">
						적용 버튼을 누르면 자동 적용 모드로 전환되어, 다음 메시지를 보낸 직후 저장됩니다. 그대로 두려면 새
						메시지를 보내거나 채팅을 초기화하세요.
					</p>
				</div>
			)
		}

		// 완료 상태 — 성공/실패 표시
		const isError = props.isError === true
		return (
			<div
				className={`workbench-tool-card ${isError ? "workbench-tool-card--error" : "workbench-tool-card--ok"}`}
			>
				<div className="workbench-tool-card__head">
					<span className="workbench-tool-card__label">{toolLabel("save_slice_to_service")}</span>
					<span className={`workbench-tool-card__badge ${isError ? "is-error-badge" : "is-applied-badge"}`}>
						{isError ? "오류" : "저장됨"}
					</span>
				</div>
				{result !== undefined && (
					<details className="workbench-tool-card__details">
						<summary className="workbench-tool-card__summary">결과 보기</summary>
						<pre className="workbench-tool-card__json">{summarizeValue(result, 300)}</pre>
					</details>
				)}
			</div>
		)
	}
}

// ─── 유틸 ────────────────────────────────────────────────────────────────────

/** suggest_slice result 에서 preview 를 추출. */
function extractSlicePreview(result: unknown): SlicePreview | null {
	if (!result || typeof result !== "object") return null
	const obj = result as Record<string, unknown>
	const preview = obj.preview
	if (!preview || typeof preview !== "object") return null
	const p = preview as Record<string, unknown>
	return {
		kinds: Array.isArray(p.kinds) ? (p.kinds as SlicePreview["kinds"]) : [],
		relations: Array.isArray(p.relations) ? (p.relations as SlicePreview["relations"]) : [],
		shapes: Array.isArray(p.shapes) ? (p.shapes as SlicePreview["shapes"]) : []
	}
}
